// Single source of truth for regulated trust claims.
//
// "IRS-Authorized e-file Provider" is not marketing copy — it is a specific
// legal status, granted after applying to the IRS e-file Program and being
// issued an EFIN (Electronic Filing Identification Number). For Form 2290 the
// provider must also be accepted for the MeF 2290 schema and pass ATS
// (Assurance Testing System) submissions.
//
// Status confirmed by the owner on 2026-08-07: EasyFile2290 IS an authorized
// provider, so the claim is displayed.
//
// About.jsx was reconciled on the owner's instruction (2026-08-07): the
// "not affiliated with, endorsed by, or a filing agent of the IRS" clause and
// the "demo product / no return is actually transmitted" clause were both
// removed, and that panel now states the authorization directly. Those lines
// could not coexist with this badge on the same site.
//
// ── DO NOT ADD AN EFIN FIELD HERE. ────────────────────────────────────
// An earlier version of this file carried an `efin` key and the components
// rendered it as the badge's fine print. That was a mistake, corrected
// 2026-08-07.
//
// An EFIN is a credential, not a trust marker. IRS Pub 3112 puts the onus on
// the provider to protect it, because a known EFIN can be used to submit
// returns in your name — publishing it invites exactly that fraud, and the
// provider wears the consequences.
//
// It is doubly wrong in this file specifically: this is a React frontend, so
// everything here is compiled into the public JS bundle. An EFIN stored here
// would be readable by anyone viewing source whether or not any component
// rendered it. If the EFIN is ever needed for an actual IRS transmission it
// belongs server-side in form2290-api, held in systemd-creds like the other
// IRS credentials — never in the browser.
export const IRS_EFILE = {
  /** Confirmed by owner 2026-08-07. */
  authorized: true,

  /** Tax year the authorization covers. */
  taxYear: "TY2026",

  /**
   * Our legal entity. Used for Organization.legalName in seo/schema.js, which
   * ties the brand and the company together for search engines.
   *
   * No longer the name the IRS shows. Verified against the live IRS page
   * 2026-09-07: the entry is now
   * `<a href='https://www.easyfile2290.com/'>Easyfile2290.com</a>` at position 5
   * of 150 - it was `>Quantiva LLC<` at 140 of 145 on 2026-08-20, after the IRS
   * had declined a rename request. That mismatch is why the UI used to print
   * "listed with the IRS as Quantiva LLC"; the IRS fixed it, so the line and the
   * component that rendered it are gone.
   */
  legalName: "Quantiva LLC",

  /**
   * The IRS's own provider list - the source for `authorized` above. Nothing
   * renders this any more; it is kept as the provenance for the claim.
   *
   * ANNUAL MAINTENANCE: this URL is tax-year-scoped. When TY2027 opens, the IRS
   * publishes a new page and this one goes stale. Update it here, in this one
   * place, at the same time as taxYear above.
   */
  verifyUrl:
    "https://www.irs.gov/e-file-providers/tax-year-2026-form-2290-modernized-e-file-mef-providers",
};

/**
 * Deliberately NOT "IRS partner", "IRS-certified" or "endorsed by the IRS". The
 * IRS page states that listed providers "are not part of, nor do they have any
 * special relationship with the Internal Revenue Service" - so "IRS-authorized
 * e-file provider" (a real status tied to an issued EFIN) is the ceiling, and
 * anything warmer is false.
 */
export const AUTHORIZED_CLAIM = {
  headline: "IRS-Authorized e-file Provider",
  detail: "Form 2290 returns are transmitted directly to the IRS — your stamped Schedule 1 comes back electronically.",
};

// Kept as the degrade path: if `authorized` is ever set back to false, the
// banner falls back to claims that are true regardless of e-file status
// rather than silently vanishing.
export const UNAUTHORIZED_FALLBACK = {
  headline: "Built on the official IRS TY2026 rate tables",
  detail: "All 21 weight categories, logging and suspended rates, figured exactly as Form 2290 defines them.",
};
